import {
  getConceptDetailSummaryById,
  getConceptRelatedKeywordCloudById,
  getConceptStatisticsById,
} from "Explore/Services/Concept";
import useExploreInfo from "Hooks/useExploreInfo";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import React, { useEffect, useState } from "react";
import styles from "./ConceptDetail.module.scss";
import BasicInformation from "./Components/BasicInformation";
import { Container } from "@mui/material";
import ConceptSummary from "./Components/ConceptSummary";
import { KeywordCloud } from "Explore/Components";
import { TIME_FILTER_TYPE } from "Explore/Config/constraint";
import { ParamsFilterKey } from "Config/filterSearchParams";
import ConceptAcademicInformation from "./Components/AcademicInformation";
import { Helmet } from "react-helmet-async";

const ConceptDetail = () => {
  const { updateSearchParams } = useFilterAndPagination();
  const { entity_id: conceptId } = useExploreInfo();
  const [summary, setSummary] = useState({ data: {}, isLoading: true });

  // Keyword cloud
  const [concept, setConcept] = useState({ data: [], isLoading: true });
  const [granularityLevel, setGranularityLevel] = useState(1);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [selectedKeywordsQueue, setSelectedKeywordsQueue] = useState([]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
    setSummary({ data: {}, isLoading: true });

    Promise.allSettled([
      getConceptStatisticsById(conceptId),
      getConceptDetailSummaryById(conceptId),
    ]).then((results) => {
      const [statsResult, summaryResult] = results;
      setSummary((prev) => ({
        data: {
          ...prev.data,
          ...(statsResult.status === "fulfilled" ? statsResult.value : {}),
          ...(summaryResult.status === "fulfilled" ? summaryResult.value : {}),
        },
        isLoading: false,
      }));
    });
  }, [conceptId]);

  useEffect(() => {
    updateSearchParams({
      [ParamsFilterKey.KEYWORD_QUERY]: selectedKeywords.map(
        (keyword) => keyword.value
      ),
    });
  }, [selectedKeywords]);

  useEffect(() => {
    getConceptRelatedKeywordCloudById({
      id: conceptId,
      keywords: selectedKeywordsQueue[selectedKeywordsQueue.length - 1] || null,
    })
      .then((res) => {
        setConcept({ data: res.results || [], isLoading: false });
      })
      .catch((err) => {
        setConcept({ data: [], isLoading: false });
      });
  }, [granularityLevel]);

  const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  const conceptName = summary.data?.name;
  
  const generateTitle = () => {
    if (!summary.data?.name) return "Concept Profile | Academic Explorer";
    const name = summary.data.name;
    const suffix = " | Academic Online";
    const maxLength = 60;

    let titleFull = `${name} — Research Topic & Academic Concept`;
    if ((titleFull + suffix).length <= maxLength) return titleFull + suffix;

    let titleShort = `${name} — Research Topic`;
    if ((titleShort + suffix).length <= maxLength) return titleShort + suffix;

    if ((name + suffix).length <= maxLength) return name + suffix;

    return `${name.substring(0, 40)}...${suffix}`;
  };
  
  const generateDescription = () => {
    const defaultDesc = "View detailed concept profile including related keywords, top scholars, institutions, and key publications.";
    const conceptName = summary.data?.name;
    if (!conceptName) return defaultDesc;

    const maxLength = 160;

    let descFull = `Explore the ${conceptName} concept including related keywords, top scholars, institutions, and key publications.`;
    if (descFull.length <= maxLength) return descFull;

    let descShort = `Explore the ${conceptName} concept including top scholars, institutions, and key publications.`;
    if (descShort.length <= maxLength) return descShort;

    return `${descShort.substring(0, 157)}...`;
  };

  return (
    <div className={styles["concept-detail"]}>
      <Helmet>
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
      </Helmet>
      <BasicInformation summary={summary} />
      <div>
        <Container maxWidth="lg" className={styles["concept-detail--body"]}>
          <ConceptSummary summary={summary} />
          <div className={styles["keyword-cloud"]}>
            <KeywordCloud
              concept={concept}
              selectedKeywords={selectedKeywords}
              setSelectedKeywords={setSelectedKeywords}
              granularityLevel={granularityLevel}
              setGranularityLevel={setGranularityLevel}
              timeFilterType={TIME_FILTER_TYPE.NONE}
              selectedKeywordsQueue={selectedKeywordsQueue}
              setSelectedKeywordsQueue={setSelectedKeywordsQueue}
              chart_width={
                window.screen.width < 1024
                  ? window.screen.width < 480
                    ? 380
                    : 500
                  : ""
              }
            />
          </div>
        </Container>
      </div>
      <div>
        <Container maxWidth="lg" className={styles["top-result"]}>
          <ConceptAcademicInformation />
        </Container>
      </div>
    </div>
  );
};

export default ConceptDetail;
